import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatImageUrl } from '../utils/imageUtils';
import { API_BASE_URL } from '../config';
import {
  CalendarIcon,
  MapPinIcon,
  ShareIcon
} from '@heroicons/react/24/outline';

interface Speaker {
  id: string;
  name: string;
  title?: string;
  company?: string;
  image?: string;
}

interface BannerTheme {
  name: string;
  background: string;
  textColor: string;
  accentColor: string;
}

const SpeakerBanner: React.FC = () => {
  const { speakerId } = useParams<{ speakerId: string }>();
  const [searchParams] = useSearchParams();
  const [speaker, setSpeaker] = useState<Speaker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');

  // URL Parameters
  const theme = searchParams.get('theme') || 'gradient';
  const size = searchParams.get('size') || 'large'; // small, medium, large
  const format = searchParams.get('format') || 'banner'; // banner, square, story
  const showTitle = searchParams.get('title') !== 'false';
  const showCompany = searchParams.get('company') !== 'false';
  const customText = searchParams.get('text');
  const watermark = searchParams.get('watermark') !== 'false';

  const themes: Record<string, BannerTheme> = {
    gradient: {
      name: 'Conference Gradient',
      background: 'linear-gradient(135deg, #00529B 0%, #0066CC 50%, #4A90E2 100%)',
      textColor: '#FFFFFF',
      accentColor: '#FFD700'
    },
    blue: {
      name: 'Conference Blue',
      background: '#00529B',
      textColor: '#FFFFFF',
      accentColor: '#FFD700'
    },
    dark: {
      name: 'Professional Dark',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      textColor: '#FFFFFF',
      accentColor: '#00529B'
    },
    minimal: {
      name: 'Clean Minimal',
      background: '#FFFFFF',
      textColor: '#1a1a1a',
      accentColor: '#00529B'
    },
    keynote: {
      name: 'Keynote Gold',
      background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
      textColor: '#1a1a1a',
      accentColor: '#00529B'
    }
  };

  useEffect(() => {
    const fetchSpeaker = async () => {
      if (!speakerId) {
        setError('Speaker ID is required');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/public/speakers?limit=1000`);
        const data = await response.json();
        const foundSpeaker = data.speakers.find((s: Speaker) => s.id === speakerId);
        
        if (!foundSpeaker) {
          setError('Speaker not found');
        } else {
          setSpeaker(foundSpeaker);
        }
      } catch (err) {
        setError('Failed to fetch speaker data');
      } finally {
        setLoading(false);
      }
    };

    const fetchLogo = async () => {
      try {
        setLogoUrl(`${API_BASE_URL}/public/logo`);
      } catch (err) {
        console.error('Failed to load logo:', err);
      }
    };

    fetchSpeaker();
    fetchLogo();
  }, [speakerId]);

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: format === 'square' ? 'w-80 h-80' : format === 'story' ? 'w-64 h-96' : 'w-[480px] h-[270px]', // Fixed 16:9 ratio
          title: 'text-lg',
          subtitle: 'text-sm',
          name: 'text-xl',
          company: 'text-sm',
          avatar: 'w-16 h-16',
          logo: 'w-12 h-12',
          padding: 'p-4'
        };
      case 'medium':
        return {
          container: format === 'square' ? 'w-96 h-96' : format === 'story' ? 'w-80 h-[480px]' : 'w-[640px] h-[360px]', // Fixed 16:9 ratio
          title: 'text-xl',
          subtitle: 'text-base',
          name: 'text-2xl',
          company: 'text-base',
          avatar: 'w-20 h-20',
          logo: 'w-16 h-16',
          padding: 'p-6'
        };
      case 'large':
      default:
        return {
          container: format === 'square' ? 'w-[500px] h-[500px]' : format === 'story' ? 'w-96 h-[600px]' : 'w-[800px] h-[450px]', // Fixed 16:9 ratio
          title: 'text-2xl',
          subtitle: 'text-lg',
          name: 'text-3xl',
          company: 'text-lg',
          avatar: 'w-24 h-24',
          logo: 'w-20 h-20',
          padding: 'p-8'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
      </div>
    );
  }

  if (error || !speaker) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="bg-white rounded-lg p-8 shadow-lg text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-600">{error || 'Speaker not found'}</p>
        </div>
      </div>
    );
  }

  const currentTheme = themes[theme] || themes.gradient;
  const sizeClasses = getSizeClasses();

  return (
    <div 
      id="banner-container"
      className={`${sizeClasses.container} ${sizeClasses.padding} rounded-xl shadow-2xl relative overflow-hidden flex flex-col justify-between`}
      style={{ 
        background: currentTheme.background,
        color: currentTheme.textColor
      }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white transform translate-x-16 -translate-y-16"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white transform -translate-x-12 translate-y-12"></div>
        <div className="absolute top-1/2 left-1/2 w-40 h-40 rounded-full bg-white transform -translate-x-1/2 -translate-y-1/2"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-between">
        {/* Header with Logo */}
        <div className="flex items-start justify-between mb-4">
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Language Intelligence Conference Logo"
              className={`${sizeClasses.logo} object-contain`}
              style={{ 
                filter: currentTheme.name === 'Clean Minimal' ? 'none' : 'brightness(0) invert(1)', // White logo for dark themes
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div className="text-right">
            <div className={`${sizeClasses.subtitle} opacity-90 font-medium`}>
              17-19 November 2025
            </div>
            <div className="text-xs opacity-75">
              Vienna, Austria
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="text-center flex-1 flex flex-col justify-center">
          {/* Main Message */}
          <div className="mb-6">
            <h1 className={`${sizeClasses.title} font-bold mb-2`}>
              {customText || "I'll be speaking at"}
            </h1>
            <h2 className={`${sizeClasses.name} font-extrabold`} style={{ color: currentTheme.accentColor }}>
              Language Intelligence 2025
            </h2>
          </div>

          {/* Speaker Info */}
          <div className="flex items-center justify-center space-x-4">
            <img
              src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
              alt={speaker.name}
              className={`${sizeClasses.avatar} rounded-full object-cover border-4 border-white border-opacity-30`}
            />
            <div className="text-left">
              <h3 className={`${sizeClasses.name} font-bold`}>
                {speaker.name}
              </h3>
              {showTitle && speaker.title && (
                <p className={`${sizeClasses.company} opacity-90`}>
                  {speaker.title}
                </p>
              )}
              {showCompany && speaker.company && (
                <p className={`${sizeClasses.company} opacity-75`}>
                  {speaker.company}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        {watermark && (
          <div className="relative z-10 flex items-center justify-between pt-4 border-t border-white border-opacity-20">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                <ShareIcon className="w-4 h-4" />
              </div>
              <span className="text-sm font-medium opacity-90">
                Language Intelligence 2025
              </span>
            </div>
            <div className="text-xs opacity-75">
              language-intelligence.com
            </div>
          </div>
        )}

        {/* Development Controls */}
        {process.env.NODE_ENV === 'development' && (
          <div className="fixed top-4 right-4 bg-white rounded-lg shadow-lg p-4 space-y-2">
            <h3 className="font-semibold text-sm text-gray-800">Banner Controls</h3>
            <div className="text-xs text-gray-600 space-y-1">
              <div>Theme: {currentTheme.name}</div>
              <div>Size: {size}</div>
              <div>Format: {format}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeakerBanner;
